import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import sessionManager from "../utils/sessionManager";
import { useToast } from "./ToastContext";

// 1. Define the Shape of the Context
export interface AuthContextType {
  isLoggedIn: boolean | null;
  login: () => void;
  logout: () => void;
  sessionExpired: boolean;
  sessionExpiredMessage: string;
  clearSessionExpired: () => void;
  validateSession: () => Promise<boolean>;
  handleSessionExpired: (message?: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string>("");
  const showToast = useToast();
  const lastSessionToastRef = useRef<{ message: string; timestamp: number } | null>(null);

  // 2. The Expiration Handler
  const handleSessionExpired = useCallback((message?: string) => {
    const msg = message || "Your session has expired. Please login again.";
    const now = Date.now();
    const lastToast = lastSessionToastRef.current;

    if (
      lastToast &&
      lastToast.message === msg &&
      now - lastToast.timestamp < 4000
    ) {
      setSessionExpired(true);
      setSessionExpiredMessage(msg);
      setIsLoggedIn(false);
      sessionManager.clearSession({ silent: true });
      return;
    }

    lastSessionToastRef.current = { message: msg, timestamp: now };
    setSessionExpired(true);
    setSessionExpiredMessage(msg);
    setIsLoggedIn(false);
    sessionManager.clearSession({ silent: true });
    showToast(msg, "error");
  }, [showToast]);

  // 3. Initialization Logic
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const sessionStatus = await sessionManager.validateSession();
        if (sessionStatus.isValid) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
          sessionManager.clearStoredSession();
        }
      } catch (err) {
        console.error("Auth Initialization Error:", err);
        setIsLoggedIn(false);
      }
    };

    initializeAuth();

    sessionManager.setSessionExpiredCallback((msg?: string) => {
      handleSessionExpired(msg);
    });
  }, [handleSessionExpired]);

  // 4. Manual Actions
  const login = useCallback(() => {
    setIsLoggedIn(true);
    setSessionExpired(false);
    setSessionExpiredMessage("");
    showToast("Welcome back!", "success");
  }, [showToast]);

  const logout = useCallback(() => {
    sessionManager.clearSession();
    setIsLoggedIn(false);
    setSessionExpired(false);
    setSessionExpiredMessage("");
    showToast("Logged out successfully", "info");
  }, [showToast]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
    setSessionExpiredMessage("");
  }, []);

  // 5. Active Validation
  const validateSession = useCallback(async () => {
    const status = await sessionManager.validateSession();
    if (!status.isValid && isLoggedIn) {
      handleSessionExpired();
    }
    return status.isValid;
  }, [isLoggedIn, handleSessionExpired]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        sessionExpired,
        sessionExpiredMessage,
        clearSessionExpired,
        validateSession,
        handleSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
